import { Card, Center, Flex, Heading } from "@chakra-ui/react";
import React, { useCallback, useState, useEffect, useRef } from "react";
import CustomButton from "../../components/ButtonComp";
import { BiArrowBack } from "react-icons/bi";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import CustomInput from "../../components/CustomInput";
import SelectImage from "../../components/SelectImage";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { baseUrl } from "../../utils/utils";

// Validation Schema using Yup
const AddProductSchema = Yup.object().shape({
  // productName: Yup.string()
  //   .min(2, "Product name must be at least 2 characters")
  //   .max(100, "Product name must be less than 100 characters")
  //   .required("Product name is required")
  //   .matches(/^[a-zA-Z0-9\s\-_]+$/, "Product name contains invalid characters"),
  productName: Yup.string()
    .min(2, "Product name must be at least 2 characters")
    .max(100, "Product name must be less than 100 characters")
    .required("Product name is required")
    .matches(/^[\s\S]*$/, "Product name contains invalid characters"),

  hsnCode: Yup.string()
    .required("HSN code is required")
    .matches(/^[0-9]{4,8}$/, "HSN code must be 4-8 digits"),

  gstPercentage: Yup.string()
    .required("GST percentage is required")
    .oneOf(
      ["0", "5", "12", "18", "28"],
      "Please select a valid GST percentage"
    ),

  category: Yup.string()
    .required("Category is required")
    .min(2, "Category must be at least 2 characters")
    .max(50, "Category must be less than 50 characters"),

  uuid: Yup.string()
    .required("Product code is required")
    .min(6, "Product code must be at least 6 characters")
    .max(8, "Product code must be less than 8 characters"),

  type: Yup.string()
    .required("Type is required")
    .min(2, "Type must be at least 2 characters")
    .max(50, "Type must be less than 50 characters"),

  material: Yup.string()
    .required("Material is required")
    .min(2, "Material must be at least 2 characters")
    .max(50, "Material must be less than 50 characters"),

  // weight: Yup.number()
  //   .required("Weight is required")
  //   // .positive("Weight must be a positive number")
  //   .typeError("Weight must be a number"),
  weight: Yup.number()
    .required("Weight is required")
    .positive("Weight must be a positive number")
    .typeError("Weight must be a number"),

  height: Yup.number()
    .required("Height is required")
    // .positive("Height must be a positive number")
    .typeError("Height must be a number"),

  diameter: Yup.number()
    .required("Diameter is required")
    // .min(0, "Diameter cannot be negative")
    .typeError("Diameter must be a number"),

  thickness: Yup.number()
    .required("Thickness is required")
    // .positive("Thickness must be a positive number")
    .typeError("Thickness must be a number"),

  waterCapacity: Yup.number()
    .required("Water Capacity is required")
    // .min(0, "Water Capacity cannot be negative")
    .typeError("Water Capacity must be a number"),

  capacity: Yup.number()
    .required("Capacity is required")
    // .min(0, "Capacity cannot be negative")
    .typeError("Capacity must be a number"),

  units: Yup.string()
    .required("Units are required")
    .oneOf(
      ["kg", "ltr", "pcs", "boxes", "tons", "grams", "ml", "units"],
      "Please select a valid unit"
    ),

  // description: Yup.string()
  //   .required("Description is required")
  //   .min(10, "Description must be at least 10 characters")
  //   .max(1000, "Description must be less than 1000 characters"),

  rsp: Yup.number()
    .required("RSP (Retail Sale Price) is required")
    .positive("RSP must be a positive number")
    .min(0.01, "RSP must be greater than 0")
    .max(999999.99, "RSP cannot exceed ₹999,999.99")
    .typeError("RSP must be a valid number"),

    fixedRate: Yup.number()
    .required("Fixed Rate is required")
    .min(0, "Fixed Rate cannot be negative")
    .max(999999.99, "Fixed Rate cannot exceed ₹999,999.99")
    .typeError("Fixed Rate must be a valid number"),
  

  securityDeposit: Yup.number()
    .required("Security Deposit is required")
    .min(0, "Security Deposit cannot be negative")
    .max(999999.99, "Security Deposit cannot exceed ₹999,999.99")
    .typeError("Security Deposit must be a valid number"),

  productImage: Yup.mixed()
    .when(["isEdit", "existingImageUrl"], {
      is: (isEdit, existingImageUrl) => isEdit && existingImageUrl,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("Product image is required"),
    })
    .test("fileSize", "File size too large", (value) => {
      return !value || value.size <= 2 * 1024 * 1024; // 2MB
    })
    .test("fileType", "Invalid file type", (value) => {
      return (
        !value || ["image/jpeg", "image/jpg", "image/png"].includes(value.type)
      );
    }),
});

const AddEditProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [productData, setProductData] = useState(null);
  const formikRef = useRef();

  // Determine if we're in edit mode
  const isEditMode = Boolean(id) || location.pathname.includes("edit");

  // Options for select fields
  const categoryOptions = [
    { value: "Commercial", label: "Commercial" },
    { value: "Domestic", label: "Domestic" },
    { value: "Regulator", label: "Regulator" },
    { value: "GasEquipment", label: "GasEquipment" },
    { value: "Lpg", label: "Lpg" },
  ];

  const typeOptions = [
    { value: "IOCL", label: "IOCL" },
    { value: "BPCL", label: "BPCL" },
    { value: "HPCL", label: "HPCL" },
    { value: "SUPERGAS", label: "SUPERGAS" },
    { value: "TOTALGAS", label: "TOTALGAS" },
    { value: "Gogas", label: "Gogas" },
    { value: "Allied", label: "Allied" },
    { value: "AEGIS", label: "AEGIS" },
  ];

  const unitOptions = [
    { value: "kg", label: "Kilograms (kg)" },
    { value: "ltr", label: "Liters (ltr)" },
    { value: "pcs", label: "Pieces (pcs)" },
    { value: "boxes", label: "Boxes" },
    { value: "tons", label: "Tons" },
    { value: "grams", label: "Grams (g)" },
    { value: "ml", label: "Milliliters (ml)" },
  ];

  const materialOptions = [
    { value: "steel", label: "Steel" },
    { value: "aluminum", label: "Aluminum" },
    { value: "plastic", label: "Plastic" },
    { value: "brass", label: "Brass" },
    { value: "iron", label: "Iron" },
    { value: "composite", label: "Composite" },
  ];

  // GST percentage options
  const gstOptions = [
    { value: "0", label: "0%" },
    { value: "5", label: "5%" },
    { value: "12", label: "12%" },
    { value: "18", label: "18%" },
    { value: "28", label: "28%" },
  ];

  // Initial form values
  const getInitialValues = () => {
    if (isEditMode && productData) {
      return {
        productName: productData.name || "",
        hsnCode: productData.hsn_no || "",
        gstPercentage: productData.gst?.toString() || "",
        category: productData.category || "",
        type: productData.type || "",
        capacity: productData.capacity || 0,
        units: productData.uom || "",
        weight: productData.wt || 0,
        height: productData.ht || 0,
        diameter: productData.dia || 0,
        thickness: productData.thick || 0,
        waterCapacity: productData.wc || 0,
        description: productData.name || "",
        material: productData.mat || "",
        rsp: productData.rsp || productData.retail_price || "",
        fixedRate: productData.fixed_rate || "", 
        securityDeposit:
          productData.security_deposit || productData.securitydeposit || 0,
        // discount: productData.discount,
        discount: "0.00",
        uuid: productData.uuid || "",
        productImage: null,
        // taxableAmount:productData.rsp - (productData.rsp*(productData.discount/100)),
        taxableAmount: productData.taxable_amount,
        isEdit: true,
        existingImageUrl: productData.url || productData.image_url || "",
      };
    }

    return {
      productName: "",
      hsnCode: "",
      gstPercentage: "",
      type: "",
      category: "",
      capacity: "0",
      units: "",
      weight: "0",
      height: "0",
      diameter: "0",
      thickness: "0",
      waterCapacity: "0",
      description: "",
      material: "",
      productImage: null,
      isEdit: false,
      existingImageUrl: "",
      rsp: "",
      fixedRate: "",
      securityDeposit: "0",
      uuid: "",
    };
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const formik = formikRef.current;
      if (!formik) return;
  
      const { values, setFieldValue } = formik;
      const rsp = parseFloat(values.rsp) || 0;
      const gst = parseFloat(values.gstPercentage) || 0;
  
      // ✅ Formula exactly as per your PHP backend
      const taxableAmount = rsp - (rsp * (gst / 100));
  
      if (
        values.taxableAmount !== taxableAmount.toFixed(2) &&
        !isNaN(taxableAmount)
      ) {
        setFieldValue("taxableAmount", taxableAmount.toFixed(2));
      }
    }, 300);
  
    return () => clearInterval(interval);
  }, []);
  

  // Fetch product data for edit mode
  const fetchProductData = useCallback(async () => {
    if (!isEditMode || !id) return;

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("action", "product_by_id");
      formData.append("id", id);

      const response = await fetch(baseUrl + "product.php", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.status === "error") {
        throw new Error(result.message || "Failed to fetch product data");
      }

      setProductData(result.reponselist || result);
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Failed to load product data", {
        position: "top-right",
        autoClose: 5000,
      });
      navigate("/products");
    } finally {
      setIsLoading(false);
    }
  }, [id, isEditMode, navigate]);

  // Fetch product data on component mount
  useEffect(() => {
    fetchProductData();
  }, [fetchProductData]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (values, { setSubmitting, setFieldError, resetForm }) => {
      try {
        setIsSubmitting(true);

        const formData = new FormData();
        const rsp = parseFloat(values.rsp) || 0;
        const gst = parseFloat(values.gstPercentage) || 0;
        const taxableAmount = rsp - (rsp * (gst / 100));

        formData.append("action", isEditMode ? "update" : "create");
        if (isEditMode && id) {
          formData.append("id", id);
        }




        formData.append("product_name", values.productName);
        formData.append("category", values.category);
        formData.append("type", values.type);
        formData.append("capacity", values.capacity.toString());
        formData.append("description", values.productName);
        formData.append("hsn", values.hsnCode);
        formData.append("uom", values.units);
        formData.append("gst", values.gstPercentage);
        formData.append("mat", values.material);
        formData.append("wt", values.weight.toString());
        formData.append("ht", values.height.toString());
        formData.append("dia", values.diameter.toString());
        formData.append("thick", values.thickness.toString());
        formData.append("wc", values.waterCapacity.toString());
        formData.append("rsp", values.rsp.toString());
        formData.append("fixed_rate", values.fixedRate.toString());
        formData.append("uuid", values.uuid);
        // formData.append("discount", values.discount);
      formData.append("unit_rate", taxableAmount.toFixed(2));
        formData.append("discount", "0.00");
        formData.append("security_deposit", values.securityDeposit.toString());

        if (values.productImage) {
          formData.append("url", values.productImage);
        }

        const response = await fetch(baseUrl + "product.php", {
          method: "POST",
          body: formData,
        });

        const result = await response.json();

        if (!response.ok || result.status === "error") {
          throw new Error(
            result.message ||
              result.error ||
              `HTTP ${response.status}: ${response.statusText}`
          );
        }

        if (result.status && result.status !== "success") {
          throw new Error(result.message || "API returned an error");
        }

        const successMessage = isEditMode
          ? `${values.productName} has been updated successfully`
          : `${values.productName} has been added to the catalog`;

        toast.success(successMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        if (!isEditMode) {
          resetForm();
        }
        navigate("/products", { replace: true });
      } catch (error) {
        console.error("Product submission error:", error);

        if (error.message.toLowerCase().includes("hsn")) {
          setFieldError("hsnCode", "Invalid HSN code");
        } else if (
          error.message.toLowerCase().includes("duplicate") ||
          error.message.toLowerCase().includes("already exists")
        ) {
          setFieldError("productName", "Product with this name already exists");
        } else if (error.message.toLowerCase().includes("unauthorized")) {
          toast.error("Session expired. Please login again.", {
            position: "top-right",
            autoClose: 5000,
          });
          navigate("/login");
        } else {
          const errorMessage = isEditMode
            ? "Failed to update product. Please try again."
            : "Failed to add product. Please try again.";

          toast.error(error.message || errorMessage, {
            position: "top-right",
            autoClose: 5000,
          });
        }
      } finally {
        setIsSubmitting(false);
        setSubmitting(false);
      }
    },
    [navigate, isEditMode, id]
  );

  // Positive number handler (allows 0 for diameter, capacity, waterCapacity)
  const handlePositiveNumberChange = (fieldName, handleChange) => (e) => {
    const value = e.target.value;

    if (value == "") {
      handleChange(fieldName)(e);
      return;
    }

    // const positiveNumberRegex = allowZero
    //   ? /^(0|[1-9]\d*)?(\.\d*)?$/ // Allows 0
    //   : /^([1-9]\d*)?(\.\d*)?$/; // Doesn't allow 0

    if (/^\d*$/.test(value)) {
      handleChange(fieldName)(e);
    }
  };

  const handlePositiveDecimalNumberChange =
    (fieldName, handleChange) => (e) => {
      const value = e.target.value;

      // Allow empty input to clear the field
      if (value === "") {
        handleChange(fieldName)(e);
        return;
      }

      // Regex to allow positive numbers with optional decimals (e.g. 12, 12.34)
      const positiveDecimalRegex = /^(?:\d+\.?\d*|\.\d+)$/;

      if (positiveDecimalRegex.test(value)) {
        handleChange(fieldName)(e);
      }
    };

  if (isLoading) {
    return (
      <Card paddingInline={5} paddingBlock={10}>
        <Center>
          <Heading fontSize="lg">Loading product data...</Heading>
        </Center>
      </Card>
    );
  }

  return (
    <>
      <Card
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        justify={"space-between"}
        paddingInline={5}
        paddingBlock={2}
      >
        <Heading fontSize={"xl"}>
          {isEditMode ? "Edit Product" : "Add Product"}
        </Heading>
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title={"Back"}
          height={"30px"}
          b_color={"#000"}
          onClick={() => navigate(-1)}
        />
      </Card>

      <Formik
        key={isEditMode ? `edit-${id}` : "add"}
        innerRef={formikRef}
        initialValues={getInitialValues()}
        validationSchema={AddProductSchema}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={true}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting: formikSubmitting,
          isValid,
          dirty,
          submitForm,
        }) => (
          <Form>
            <Card paddingInline={5}>
              {/* Row 1: Product Description + HSN Code */}
              <CustomInput
                label1={"Product Description"}
                placeholder1={"Enter product description"}
                value1={values.productName}
                onChange1={handleChange("productName")}
                onBlur1={handleBlur("productName")}
                error1={touched.productName && errors.productName}
                required1={true}
                label2={"HSN Code"}
                placeholder2={"Enter 4-8 digit HSN code"}
                value2={values.hsnCode}
                onChange2={handleChange("hsnCode")}
                onBlur2={handleBlur("hsnCode")}
                error2={touched.hsnCode && errors.hsnCode}
                required2={true}
              />

              {/* Row 2: GST Percentage + Units */}
              <CustomInput
                label1={"GST Percentage"}
                value1={values.gstPercentage}
                onChange1={handleChange("gstPercentage")}
                onBlur1={handleBlur("gstPercentage")}
                select1={true}
                options1={gstOptions}
                placeholder1={"Select GST Percentage"}
                error1={touched.gstPercentage && errors.gstPercentage}
                required1={true}
                label2={"Units"}
                value2={values.units}
                onChange2={handleChange("units")}
                onBlur2={handleBlur("units")}
                select2={true}
                options2={unitOptions}
                placeholder2={"Select Units"}
                error2={touched.units && errors.units}
                required2={true}
              />

              {/* Row 3: Category + Type */}
              <CustomInput
                label1={"Category"}
                value1={values.category}
                onChange1={handleChange("category")}
                onBlur1={handleBlur("category")}
                select1={true}
                options1={categoryOptions}
                placeholder1={"Select Category"}
                error1={touched.category && errors.category}
                required1={true}
                label2={"Type"}
                value2={values.type}
                onChange2={handleChange("type")}
                onBlur2={handleBlur("type")}
                select2={true}
                options2={typeOptions}
                placeholder2={"Select Type"}
                error2={touched.type && errors.type}
                required2={true}
              />

              {/* Row 4: Material + Weight */}
              <CustomInput
                label1={"Material"}
                value1={values.material}
                onChange1={handleChange("material")}
                onBlur1={handleBlur("material")}
                select1={true}
                options1={materialOptions}
                placeholder1={"Select Material"}
                error1={touched.material && errors.material}
                required1={true}
                label2={"Weight"}
                subLabel2={"kg"}
                placeholder2={"Enter weight"}
                value2={values.weight}
                onChange2={handlePositiveDecimalNumberChange(
                  "weight",
                  handleChange
                )}
                onBlur2={handleBlur("weight")}
                error2={touched.weight && errors.weight}
                required2={true}
              />

              {/* Row 5: Height + Thickness */}
              <CustomInput
                label1={"Height"}
                subLabel1={"mm"}
                placeholder1={"Enter height"}
                value1={values.height}
                onChange1={handlePositiveDecimalNumberChange(
                  "height",
                  handleChange
                )}
                onBlur1={handleBlur("height")}
                error1={touched.height && errors.height}
                required1={true}
                label2={"Thickness"}
                subLabel2={"mm"}
                placeholder2={"Enter thickness"}
                value2={values.thickness}
                onChange2={handlePositiveDecimalNumberChange(
                  "thickness",
                  handleChange
                )}
                onBlur2={handleBlur("thickness")}
                error2={touched.thickness && errors.thickness}
                required2={true}
              />

              {/* Row 6: Diameter + Capacity (Allow 0) */}
              <CustomInput
                label1={"Diameter"}
                subLabel1={"mm"}
                placeholder1={"Enter diameter (0 allowed)"}
                value1={values.diameter}
                onChange1={handlePositiveDecimalNumberChange(
                  "diameter",
                  handleChange
                )}
                onBlur1={handleBlur("diameter")}
                error1={touched.diameter && errors.diameter}
                required1={true}
                label2={"Capacity"}
                subLabel2={values.units}
                placeholder2={"Enter capacity (0 allowed)"}
                value2={values.capacity}
                onChange2={handlePositiveDecimalNumberChange(
                  "capacity",
                  handleChange
                )}
                onBlur2={handleBlur("capacity")}
                error2={touched.capacity && errors.capacity}
                required2={true}
              />

              {/* Row 7: Water Capacity + Product Code */}
              <CustomInput
                label1={"Water Capacity"}
                subLabel1={"ltr"}
                placeholder1={"Enter water capacity (0 allowed)"}
                value1={values.waterCapacity}
                onChange1={handlePositiveDecimalNumberChange(
                  "waterCapacity",
                  handleChange
                )}
                onBlur1={handleBlur("waterCapacity")}
                error1={touched.waterCapacity && errors.waterCapacity}
                required1={true}
                label2={"Product Code"}
                placeholder2={"Enter 6-8 character product code"}
                value2={values.uuid}
                onChange2={handleChange("uuid")}
                onBlur2={handleBlur("uuid")}
                error2={touched.uuid && errors.uuid}
                required2={true}
              />

              {/* Row 8: RSP + Security Deposit */}
              {/* <CustomInput
                label1={"RSP"}
                placeholder1={"Enter Retail Selling Price"}
                value1={values.rsp}
                onChange1={handleChange("rsp")}
                onBlur1={handleBlur("rsp")}
                error1={touched.rsp && errors.rsp}
                required1={true}
                label2={"Discount (%)"}
                placeholder2={"Enter discount %"}
                value2={values.discount}
                onChange2={(e) => {
                  let value = e.target.value;

                  // Allow empty input for clearing
                  if (value === "") {
                    setFieldValue("discount", "");
                    return;
                  }

                  const numericValue = parseFloat(value);
                  if (!isNaN(numericValue)) {
                    if (numericValue > 100) {
                      toast.warning("Discount cannot exceed 100%");
                      setFieldValue("discount", "100");
                    } else {
                      setFieldValue("discount", value);
                    }
                  }
                }}
                onBlur2={handleBlur("discount")}
                error2={touched.discount && errors.discount}
                required2={true}
              /> */}

<CustomInput
  label1={"RSP"}
  placeholder1={"Enter Retail Selling Price"}
  value1={values.rsp}
  onChange1={handleChange("rsp")}
  onBlur1={handleBlur("rsp")}
  error1={touched.rsp && errors.rsp}
  required1={true}
  hideSecondInput={true} // optional, if CustomInput supports conditional second field
  label2={"Unit Rate"}
  placeholder2={"Auto-calculated"}
  value2={values.taxableAmount}
  onChange2={handleChange("taxableAmount")}
  onBlur2={handleBlur("taxableAmount")}
  error2={touched.taxableAmount && errors.taxableAmount}
  required2={true}
  disabled2={true}
/>


<CustomInput
  label1={"Fixed Rate"}
  placeholder1={"Enter Fixed Rate"}
  value1={values.fixedRate}
  onChange1={handleChange("fixedRate")}
  onBlur1={handleBlur("fixedRate")}
  error1={touched.fixedRate && errors.fixedRate}
  required1={true}
  label2={"Security Deposit"}
  placeholder2={"Enter Security Deposit in ₹"}
  value2={values.securityDeposit}
  onChange2={handleChange("securityDeposit")}
  onBlur2={handleBlur("securityDeposit")}
  error2={touched.securityDeposit && errors.securityDeposit}
  required2={true}
/>

              {/* Product Image */}
              <Flex w={["100%", "100%", "49.5%"]}>
                <SelectImage
                  label={
                    isEditMode
                      ? "Update Product Image (Optional)"
                      : "Product Image"
                  }
                  value={values.productImage}
                  existingImage={values.existingImageUrl}
                  onChange={(file) => setFieldValue("productImage", file)}
                  error={touched.productImage && errors.productImage}
                  required={!isEditMode}
                  doctype="image/jpeg, image/jpg, image/png"
                  maxSize={2 * 1024 * 1024}
                />
              </Flex>

              {/* Submit Button */}
              <Center marginBlock={5}>
                <CustomButton
                  title={
                    isSubmitting || formikSubmitting
                      ? isEditMode
                        ? "Updating..."
                        : "Submitting..."
                      : isEditMode
                      ? "Update Product"
                      : "Add Product"
                  }
                  onClick={submitForm}
                  disabled={isSubmitting || formikSubmitting || !isValid}
                />
              </Center>
            </Card>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddEditProduct;
